import { useSelector } from 'react-redux';
import { RootState } from '../../ApplicationState';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { selectClassroom } from '../../Classrooms';
import { getGuidedReadingLevelRange, isUserHasKey } from '../../GlobalFunctions';
import FormikControl from '../../components/FormikContainer/FormikControl';
import TextButton from '../../components/TextButton/TextButton';
import { StudentProxyType } from '../../../peekapak-types/DataProtocolTypes';

interface Props {
  student: StudentProxyType;
  readingLevels: string[];
  onSubmit: (value: FormValues) => void;
}
interface FormValues {
  name: string;
  readingLevel: string;
  email?: string;
}

const validationSchema = Yup.object({
  name: Yup.string().min(4, 'Student name must be at least 4 characters').required('Required'),
  readingLevel: Yup.string().required('Required'),
  email: Yup.string().email('Please enter a valid email address'),
});

const EditStudentEditControl = ({ student, readingLevels, onSubmit }: Props): JSX.Element => {
  let fullName = student.firstName;
  if (student.middleName) fullName += ' ' + student.middleName;
  if (student.lastName) fullName += ' ' + student.lastName;

  const initialValues: FormValues = {
    name: '' || fullName,
    readingLevel: '' || student.readingLevel,
    email: student.email || '',
  };

  const classroom = useSelector((state: RootState) => {
    const selectedClassroom = selectClassroom(state);
    return selectedClassroom;
  });

  const currentReadAloudLevelScheme = classroom.readAloudLevelScheme || 'fountasAndPinnell';

  const getMappedReadingLevels = () => {
    return readingLevels.map((level) => {
      const mappedLevel = getGuidedReadingLevelRange(parseInt(level), currentReadAloudLevelScheme);
      return { key: mappedLevel, value: mappedLevel };
    });
  };

  const isMiddleSchool = () => {
    if (classroom.grade && parseInt(classroom.grade) > 5) return true;
    return false;
  };

  return isUserHasKey('KEY_MYPEEKAVILLE_LESSONS') ? (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      {() => (
        <Form>
          <FormikControl
            control='input'
            type='text'
            name='name'
            label='Name'
            onBlur={(event: React.ChangeEvent<HTMLInputElement>) => event.preventDefault()}
          />
          {!isMiddleSchool() && (
            <FormikControl
              control='select'
              label='Reading Level'
              name='readingLevel'
              options={getMappedReadingLevels()}
              onBlur={(event: React.ChangeEvent<HTMLSelectElement>) => event.preventDefault()}
            />
          )}
          <FormikControl
            control='input'
            type='email'
            name='email'
            label='School Email Address'
            onBlur={(event: React.ChangeEvent<HTMLInputElement>) => event.preventDefault()}
          />
          <TextButton uppercase blue bold underlineHover type='submit'>
            + Update Student
          </TextButton>
        </Form>
      )}
    </Formik>
  ) : (
    <Formik initialValues={initialValues} onSubmit={onSubmit}>
      {() => (
        <Form>
          <FormikControl disabled control='input' type='text' name='name' label='Name' />
          <FormikControl disabled control='input' type='email' name='email' label='School Email Address' />
        </Form>
      )}
    </Formik>
  );
};

export default EditStudentEditControl;
